/**
 * ValoraaBead shop pieces for the home page (sections/vb-product-carousel.liquid,
 * snippets/vb-portrait-card.liquid).
 *
 * <vb-add-to-cart> wraps a plain `<form action="/cart/add">` (it works without JavaScript) and
 * upgrades it to an in-page add that speaks Horizon's cart protocol: it dispatches the standard
 * CartLinesUpdateEvent with a promise, posts to /cart/add.js with the cart sections to re-render,
 * then resolves the promise with the fresh cart. Horizon's cart drawer, cart count and cart items
 * update (and the drawer opens when "Auto open cart drawer" is on) exactly as they do for the
 * product page form.
 *
 * Button states: "Adding", then "Added" for two seconds. Screen readers hear "<product> added to
 * your cart" (or the store's error) through the section's one shared polite live region
 * (`[data-vb-atc-status]`), so the page carries one region per carousel, not one per card.
 *
 * Keyboard: in each shop carousel only the centred card's name and Add to cart are in the Tab
 * order (a roving tabindex). Left/Right arrows (the carousel's own keys) or its arrow buttons move
 * to the other cards, and focus follows the new centre card, so one carousel is five Tab stops
 * (name, Add to cart, previous, pause, next) instead of two per product.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ADDED_MS = 2000;

class VbAddToCart extends HTMLElement {
  /** @type {AbortController} */
  #abort = new AbortController();
  #busy = false;
  #timer = 0;

  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[data-vb-atc]');
    this.label = this.querySelector('[data-vb-atc-label]');
    this.status =
      this.closest('[data-vb-shop-carousel]')?.querySelector('[data-vb-atc-status]') ?? sharedStatus();
    if (!this.form || !this.button) return;
    this.defaultLabel = this.label?.textContent ?? '';
    this.form.addEventListener('submit', (event) => this.#onSubmit(event), { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
    clearTimeout(this.#timer);
  }

  /** @param {SubmitEvent} event */
  async #onSubmit(event) {
    event.preventDefault();
    if (this.#busy) return;
    this.#busy = true;
    clearTimeout(this.#timer);
    this.#setState('adding');

    const formData = new FormData(this.form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity: Number(formData.get('quantity')) || 1 }],
        promise: deferred.promise,
      })
    );

    const routes = window.Theme?.routes ?? {};
    try {
      const response = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const result = await response.json();
      const cart = await this.#fetchCart(routes);

      if (result.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'vb-add-to-cart' },
        });
        this.#setState('error', result.description || result.message);
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          sections: result.sections,
          source: 'vb-add-to-cart',
          itemCount: 1,
          productId: this.form.dataset.productId,
        },
      });
      this.#setState('added');
    } catch (error) {
      deferred.reject(error);
      this.#setState('error');
    } finally {
      this.#busy = false;
    }
  }

  /** Reuse the cart items component's fetch when there is one (it de-duplicates requests). */
  async #fetchCart(routes) {
    const items = document.querySelector('cart-items-component');
    if (items && typeof items.fetchCartData === 'function') return items.fetchCartData();
    const response = await fetch(`${routes.cart_url || '/cart'}.json`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Cart ${response.status}`);
    return response.json();
  }

  /** @param {'adding'|'added'|'error'|'idle'} state @param {string} [message] */
  #setState(state, message) {
    const { button, label, status } = this;
    const data = button.dataset;
    this.dataset.state = state;
    button.setAttribute('aria-busy', String(state === 'adding'));
    const text =
      state === 'adding'
        ? data.labelAdding
        : state === 'added'
          ? data.labelAdded
          : state === 'error'
            ? message || data.labelError
            : this.defaultLabel;
    if (label) label.textContent = (state === 'error' ? data.labelError : text) || this.defaultLabel;
    // Announce the outcome only (not "Adding"), with the product's name, in the shared region.
    if (status && (state === 'added' || state === 'error')) {
      const spoken = state === 'added' ? data.statusAdded || text : text;
      status.textContent = '';
      requestAnimationFrame(() => (status.textContent = spoken || ''));
    }
    if (state === 'added' || state === 'error') {
      this.#timer = window.setTimeout(() => this.#setState('idle'), ADDED_MS);
    }
  }
}

/** A page-level polite region, for a card rendered outside a shop carousel section. */
let pageStatus = null;
function sharedStatus() {
  if (pageStatus?.isConnected) return pageStatus;
  pageStatus = document.createElement('p');
  pageStatus.className = 'visually-hidden';
  pageStatus.setAttribute('role', 'status');
  pageStatus.setAttribute('aria-live', 'polite');
  document.body.append(pageStatus);
  return pageStatus;
}

if (!customElements.get('vb-add-to-cart')) customElements.define('vb-add-to-cart', VbAddToCart);

/**
 * Roving tabindex for a shop carousel: only the real slide twinned with the centred item keeps its
 * links and buttons in the Tab order. When the centre changes while focus is on a card (arrow
 * keys; autoplay is paused then), focus moves to the same control in the new centre card.
 * Without JavaScript every card stays tabbable.
 */
const FOCUSABLE = 'a[href]:not([aria-hidden="true"]), button';

const rove = (carousel) => {
  const track = carousel.querySelector('[data-vb-track]');
  if (!track || carousel.hasAttribute('data-vb-rove')) return;
  carousel.setAttribute('data-vb-rove', '');
  const realSlides = () => [...track.children].filter((el) => el.matches('[data-vb-slide]:not([data-vb-clone])'));

  const sync = () => {
    const active = track.querySelector(':scope > .is-active');
    if (!(active instanceof HTMLElement)) return;
    const slides = realSlides();
    const twin = active.matches('[data-vb-clone]')
      ? slides.find((el) => el.dataset.vbIndex === active.dataset.vbIndex)
      : active;
    if (!twin) return;
    const focused = document.activeElement;
    const from = focused instanceof HTMLElement && track.contains(focused) ? focused.closest('[data-vb-slide]') : null;
    const position = from && from !== twin ? [...from.querySelectorAll(FOCUSABLE)].indexOf(focused) : -1;
    for (const slide of slides) {
      for (const el of slide.querySelectorAll(FOCUSABLE)) {
        if (slide === twin) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      }
    }
    // A clone is centred only for a moment (the loop then jumps to its twin): move focus once the
    // real twin is centred, so focusing it does not set off a long scroll back.
    if (position >= 0 && active === twin) {
      const target = twin.querySelectorAll(FOCUSABLE)[position] ?? twin.querySelector(FOCUSABLE);
      if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    }
  };

  new MutationObserver(sync).observe(track, { subtree: true, attributes: true, attributeFilter: ['class'] });
  sync();
};

/**
 * Carousel photos are lazy, but a carousel that moves on its own (or is swiped) must never reveal
 * a blank card. When a shop carousel comes within about one screen of the viewport, its card
 * photos switch to eager loading; the hover photos follow on the first pointer or focus inside.
 */
const warm = (root, selector) => {
  for (const img of root.querySelectorAll(selector)) {
    if (img.loading === 'lazy') img.loading = 'eager';
  }
};

const observer =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            observer.unobserve(entry.target);
            warm(entry.target, '.vb-pcard__img:not(.vb-pcard__img--hover)');
          }
        },
        { rootMargin: '100% 0px' }
      )
    : null;

const initShopCarousels = (scope = document) => {
  for (const section of scope.querySelectorAll('[data-vb-shop-carousel]:not([data-vb-warm])')) {
    section.setAttribute('data-vb-warm', '');
    observer?.observe(section);
    const carousel = section.querySelector('vb-carousel');
    if (carousel) {
      // <vb-carousel> builds its clones and centres a slide when it upgrades; wait for that.
      customElements.whenDefined('vb-carousel').then(() => requestAnimationFrame(() => rove(carousel)));
    }
    const hover = () => warm(section, '.vb-pcard__img--hover');
    section.addEventListener('pointerenter', hover, { once: true });
    section.addEventListener('focusin', hover, { once: true });
  }
};

initShopCarousels();
document.addEventListener('shopify:section:load', (event) => initShopCarousels(event.target));
